import { Context, FirebaseV9 } from '@bitiotic/bitiotic';
import { Game, GameOptions, Lobby, Persistent } from '@bitiotic/numbet';
import { JSX, Show, createSignal } from 'solid-js';
import { GameLink } from './Routing';
import { formatCurrency, SpinButton } from './Widgets';

export function getEmailShareHref(ctx: Context, gameId: string | null): string {
    if (gameId === null) {
        const subj = encodeURIComponent('Check out Numbets!');
        return `mailto:?subject=${subj}`;
    }

    const gameURL = getGameURL(ctx, gameId);
    const subj = encodeURIComponent('Join my Numbets game!');
    const body = encodeURIComponent(`
        Click this link to join the game: ${gameURL}
    `);
    return `mailto:?subject=${subj}&body=${body}`;
}

export function getGameURL(ctx: Context, gameId: string | null): string {
    const u = new URL(window.location.href);

    if (gameId === null) {
        u.pathname = '/';
    } else {
        u.pathname = `game/${gameId}`;
    }

    return u.href;
}

function strToNum(s: string | undefined, defaultNum: number, min: number, max: number): number | null {
    let n: number = defaultNum;

    if (s !== undefined) {
        n = Number(s);
        if (isNaN(n)) {
            n = defaultNum;
        }
    }

    if (n < min) {
        return null;
    }

    if (n > max) {
        return null;
    }

    return n;
}


export function NewGame(props: {
    ctx: Context,
    lobby: Lobby,
    firestore: FirebaseV9.Firestore,
    fbFunc: FirebaseV9.FirebaseFunc,
    playerPath: 'unknown' | Persistent.PlayerPath,
}): JSX.Element {
    const [getNewlyCreatedGamePath, setNewlyCreatedGamePath] = createSignal<Persistent.GamePath | null>(null);
    const [getCreateDisabled, setCreateDisabled] = createSignal<boolean>(false);
    const [getRoundCountUI, setRoundCountUI] = createSignal<string>('');
    const [getRoundBetCount, setRoundBetCount] = createSignal<string>('');
    const [getRoundBetLimit, setRoundBetLimit] = createSignal<string>('');

    const gameOptions = new GameOptions(props.ctx);

    const MAX_BET_GRANT = 400;
    const MAX_BET_LIMIT = gameOptions.betSpotCount(props.ctx);

    function createNewGame(): Promise<void> {
        const ctx = props.ctx.newCtx('newGame');
        const pth = props.playerPath;
        if (!pth || pth === 'unknown') {
            throw ctx.newError('Should not be able to CreateGame without a current player');
        }

        const roundCt = strToNum(getRoundCountUI(), -1, 1, GameOptions.MAX_ROUNDS_PER_GAME);
        if (roundCt === null) {
            return Promise.resolve();
        }

        const betGrant = strToNum(getRoundBetCount(), -1, 100, MAX_BET_GRANT);
        if (betGrant === null) {
            return Promise.resolve();
        }

        const betLimit = strToNum(getRoundBetLimit(), -1, 1, MAX_BET_LIMIT);
        if (betLimit === null) {
            return Promise.resolve();
        }

        setCreateDisabled(true);

        gameOptions.setGrantPerRound(ctx, betGrant);
        gameOptions.setBetsPerRound(ctx, betLimit);
        gameOptions.setRoundsPerGame(ctx, roundCt);

        return Game.startNew(ctx, pth, gameOptions, props.firestore, props.fbFunc)
            .then(g => {
                ctx.log('New game', g.getGamePath(), 'created');
                setNewlyCreatedGamePath(g.getGamePath());
            })
            .catch(err => {
                ctx.log('Error creating new game:', err);
            })
            .finally(() => {
                setTimeout(() => setCreateDisabled(false),
                    250);
            });
    }

    setRoundCountUI(`${gameOptions.roundsPerGame(props.ctx)}`);
    setRoundBetCount(`${gameOptions.grantPerRound(props.ctx)}`);
    setRoundBetLimit(`${gameOptions.betsPerRound(props.ctx)}`);

    return <div class="mx-8 md:max-w-3xl">
        <div class="font-bold text-xl">New Game</div>
        <div class="px-3 py-5">
            <div>
                Number of rounds: <span class="w-[3ch]">{getRoundCountUI()}</span>
            </div>
            <input
                id="new-game-max-round-count"
                autocomplete="off"
                minlength="1"
                maxlength="3"
                class="appearance-none border rounded text-gray-700 leading-tight focus:outline-none focus:shadow-outline range-lg"
                type="range"
                min="1"
                max={GameOptions.MAX_ROUNDS_PER_GAME}
                value={getRoundCountUI()}
                onInput={ev => { setRoundCountUI(ev.target.value); }}></input>
            <div class="px-3">
                The number of rounds in the game.  There is one question per round.  Five to seven rounds is recommended.
            </div>
        </div>
        <div class="px-3 py-5">
            <div>
                Bet grant (per-round): <span class="w-[3ch]">{formatCurrency(Number(getRoundBetCount()))}</span>
            </div>
            <input
                id="new-game-bet-grant"
                autocomplete="off"
                minlength="1"
                maxlength="3"
                class="appearance-none border rounded text-gray-700 leading-tight focus:outline-none focus:shadow-outline range-lg"
                type="range"
                min="100"
                max={MAX_BET_GRANT}
                step="100"
                value={getRoundBetCount()}
                onInput={ev => { setRoundBetCount(ev.target.value); }}></input>
            <div class="px-3">
                The grant, per-round of money players can bet.  $200 is recommended.
            </div>
        </div>
        <div class="px-3 py-5">
            <div>
                Bet count limit: <span class="w-[3ch]">{getRoundBetLimit()}</span>
            </div>
            <input
                id="new-game-bet-limit"
                autocomplete="off"
                minlength="1"
                maxlength="3"
                class="appearance-none border rounded text-gray-700 leading-tight focus:outline-none focus:shadow-outline range-lg"
                type="range"
                min="1"
                max={MAX_BET_LIMIT}
                value={getRoundBetLimit()}
                onInput={ev => { setRoundBetLimit(ev.target.value); }}></input>
            <div class="px-3">
                The number of distinct bet spots each player can place a bet on in a round.  Two is recommended.
            </div>
        </div>
        <SpinButton
            color="green"
            disabled={getCreateDisabled()}
            onClick={createNewGame}
        >Create New Game</SpinButton>
        <Show when={getNewlyCreatedGamePath()}>
            <div class="font-bold text-xl">Created Game</div>
            <div class="p-3">
                Link:&nbsp;
                <GameLink ctx={props.ctx} gamePath={getNewlyCreatedGamePath()}>{getNewlyCreatedGamePath()}</GameLink>
            </div >
        </Show>
    </div>;
}